import React, { useContext, useMemo, useState } from 'react';
import EDTypography from '../../../../../../../BaseComponents/Typography/EDTypography';
import EDView from '../../../../../../../BaseComponents/View/EDView';
import { ActiveDatabaseContext } from '../../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import {
  IBusinessCollectionProps,
  IObjektVy,
  IObjektVyComp,
} from '../../../../../../../Types/ObjektVy';
import mergeSort from '../../../../../../../Utils/mergeSort';
import { AUTOMATICALLY_ADDED_VALUE_PREFIX } from '../../../../../Leverantor/ObjektVyer/FakturaDel/Components/FakturaDelContextManager/FakturaDelContextManager';
import { BusinessDataFormContext } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import {
  mergeBusinessDataFormValues,
  parseText,
} from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import { BokforderQuery_node_TEDEKBokforder__Associations_Meddelande_All } from '../../Queries/_QueryTypes/BokforderQuery';
import { BokforderType__Associations_Meddelande_All } from '../../Queries/_QueryTypes/BokforderType';
import BokforderContext from '../BokforderContext/BokforderContext';
import BokforderMessage from './Components/BokforderMessage/BokforderMessage';
import {
  compareBokforderMessage,
  isSenderOfMessage,
} from './Utils/BokforderMessagesUtils';
import EDBox from '../../../../../../../BaseComponents/EDBox/EDBox';
import { TEDEKBokforderSTATUSEnum } from '../../../../../../../globalQueryTypes';

interface IProps extends IBusinessCollectionProps {
  messages: BokforderType__Associations_Meddelande_All[];
}

const BokforderMessagesComp: IObjektVyComp = {
  caption: '',
  comp: props => <BokforderMessagesView {...(props as IProps)} />,
};

export const BokforderMessagesView: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const formContext = useContext(BusinessDataFormContext);
  const bokforderContext = useContext(BokforderContext);
  const { userID } = useContext(ActiveDatabaseContext);
  const [originalValue, setOriginalValue] = useState('');

  const dataMergedWithChanges = useMemo(
    () =>
      mergeBusinessDataFormValues(
        parseText(formContext?.data),
        parseText(formContext?.formState?.values ?? {})
      ),
    [formContext?.data, formContext?.formState.values]
  );

  const savedMessages: BokforderQuery_node_TEDEKBokforder__Associations_Meddelande_All[] =
    useMemo(
      () =>
        props.messages
          ? mergeSort(props.messages, compareBokforderMessage)
          : mergeSort(
              (dataMergedWithChanges._Associations?.Meddelande ?? []).filter(
                (
                  value: BokforderQuery_node_TEDEKBokforder__Associations_Meddelande_All
                ) => !value.id.startsWith(AUTOMATICALLY_ADDED_VALUE_PREFIX)
              ),
              compareBokforderMessage
            ).filter(
              (message: any) =>
                !formContext?.formState.values?._Associations?.Meddelande_Delete_Values?.some(
                  (deletedMessage: any) => deletedMessage.id === message.id
                )
            ),
      [
        dataMergedWithChanges._Associations?.Meddelande,
        formContext?.formState.values?._Associations?.Meddelande_Delete_Values,
        props.messages,
      ]
    );

  const newlyCreatedMessages: BokforderQuery_node_TEDEKBokforder__Associations_Meddelande_All[] =
    useMemo(
      () =>
        (dataMergedWithChanges._Associations?.Meddelande ?? [])
          .filter(
            (
              value: BokforderQuery_node_TEDEKBokforder__Associations_Meddelande_All
            ) => value.id.startsWith(AUTOMATICALLY_ADDED_VALUE_PREFIX)
          )
          .reverse(),
      [dataMergedWithChanges._Associations?.Meddelande]
    );

  let isLastSender = props.messages
    ? false
    : savedMessages.length === 0
    ? false
    : isSenderOfMessage(savedMessages?.[0], userID ?? '');

  const allowedStatuses = [
    TEDEKBokforderSTATUSEnum.UTKAST,
    TEDEKBokforderSTATUSEnum.EJ_GODKAND,
    TEDEKBokforderSTATUSEnum.VANTAR_PA_ATTEST,
  ];

  const showButtons =
    allowedStatuses.includes(formContext?.formState.currentValues?.STATUS) &&
    !formContext?.isNew;

  return (
    <>
      <EDBox sx={{ width: '100%' }}>
        {newlyCreatedMessages.length > 0 &&
          newlyCreatedMessages.map((message, index) => {
            const isEditMode = bokforderContext.editMessageID === message.id;

            const deleteOrUndoMessage = (
              event: React.MouseEvent<HTMLElement, MouseEvent>
            ) => {
              event.stopPropagation();
              bokforderContext.setEditMessageID('');
              bokforderContext.deleteMessage(message.id);
            };

            const hasChanged = message.MEDDELANDE !== '';

            return (
              <BokforderMessage
                key={index}
                deleteOrUndoMessage={deleteOrUndoMessage}
                deleteOrUndoMessageTooltip={'Ta bort meddelandet'}
                hasChanged={hasChanged}
                hasMessageProp={Boolean(props.messages)}
                index={index}
                isEditMode={isEditMode}
                message={message}
                setOriginalValue={setOriginalValue}
                showEditButtons={!isEditMode && showButtons}
              />
            );
          })}
        {savedMessages.length > 0 &&
          savedMessages.map((message, index) => {
            const isEditMode = bokforderContext.editMessageID === message.id;

            const showEditButtons =
              isLastSender && index === 0 && !isEditMode && showButtons;

            const hasChanged = (message.MEDDELANDE ?? '') !== originalValue;

            const deleteOrUndoMessage = (
              event: React.MouseEvent<HTMLElement, MouseEvent>
            ) => {
              event.stopPropagation();
              bokforderContext.addOrUpdateMessage(
                originalValue,
                message.id,
                message._version
              );
              setOriginalValue('');
              bokforderContext.setEditMessageID('');
            };

            return (
              <BokforderMessage
                key={index}
                deleteOrUndoMessage={deleteOrUndoMessage}
                deleteOrUndoMessageTooltip={'Ångra ändringar'}
                hasChanged={hasChanged}
                hasMessageProp={Boolean(props.messages)}
                index={index}
                isEditMode={isEditMode}
                message={message}
                setOriginalValue={setOriginalValue}
                showEditButtons={showEditButtons}
              />
            );
          })}
        {savedMessages.length <= 0 && newlyCreatedMessages.length <= 0 && (
          <EDView gutter>
            <EDTypography>Det finns inga meddelanden att visa här</EDTypography>
          </EDView>
        )}
      </EDBox>
    </>
  );
};

export const BokforderMessages: IObjektVy = {
  type: 'ObjektVy',
  id: 'BokforderMessages',
  info: {},
  def: () => BokforderMessagesComp,
};
